import { Breadcrumbs, Page, PageHeader } from '@cint/wonderment';
import { useMemo } from 'react';

import { useFetchData } from '@/api/apiHooks';
import { useReportContext } from '@/context/ReportContext';
import { Charts, Metrics } from '@/pages/MarketingRecruitmentReport/components';
import { URLS } from '@/utils/urls';

import { ReportDateFilters } from './ReportDateFilters';
import { ReportHeader } from './ReportHeader';

const convertToDateString = (date: Date): string => {
  if (!date) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const RecruitmentReportLayout = () => {
  const { filters, selectedCompany, selectedPanel, setFilters } =
    useReportContext();

  const formattedFromDate = convertToDateString(filters.selectedDates?.from);
  const formattedToDate = convertToDateString(filters.selectedDates?.to);

  const selectedSources = useMemo(
    () => filters.sourceFilterState || [],
    [filters.sourceFilterState]
  );

  const marketingCampaignDataQueryString = useMemo(() => {
    const params = new URLSearchParams();
    Array.isArray(selectedSources) &&
      selectedSources.forEach((source) =>
        params.append('recruitment_sources', source)
      );
    selectedPanel &&
      selectedPanel !== 'all' &&
      params.append('panel_ids', selectedPanel);
    formattedFromDate && params.append('from_date', formattedFromDate);
    formattedToDate && params.append('to_date', formattedToDate);
    filters?.selectedInterval &&
      params.append('time_interval', filters.selectedInterval);
    return params.toString();
  }, [
    selectedSources,
    selectedPanel,
    formattedFromDate,
    formattedToDate,
    filters.selectedInterval,
  ]);

  const {
    isLoading: isMarketingCampaignDataLoading,
    data: marketingCampaignData,
    error: marketingCampaignDataError,
  } = useFetchData<any>({
    url: URLS.getMarketingCampaignReportData(
      selectedCompany,
      marketingCampaignDataQueryString
    ),
    enabled: !!selectedCompany && !!selectedPanel && !!selectedSources?.length,
  });

  return (
    <>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            iconName="ChartBar"
            segments={[{ title: 'Recruitment insights' }]}
          />
        }
        className="px-8"
      />
      <Page>
        <ReportHeader title="Recruitment Insights" reportName="marketing" />
        <div className="w-full px-4">
          <div className="flex justify-end mb-10">
            <ReportDateFilters filters={filters} onFiltersChange={setFilters} />
          </div>
          <Metrics
            isMarketingCampaignDataLoading={isMarketingCampaignDataLoading}
            marketingCampaignData={marketingCampaignData}
            marketingCampaignDataError={!!marketingCampaignDataError}
          />
          <Charts
            marketingCampaignData={marketingCampaignData}
            isDataLoading={isMarketingCampaignDataLoading}
            marketingCampaignDataError={!!marketingCampaignDataError}
          />
        </div>
      </Page>
    </>
  );
};
